import { classes } from "portal/theme/theme";
import ChangeIcon from "@mui/icons-material/ArrowForward";
import React, { ReactNode } from "react";

export type StyleChange = "added" | "removed";

export type ValueChangeRender<T> = (
  value: T,
  options: { styleChange?: StyleChange }
) => ReactNode;

interface ValueChangeProps<T> {
  /** Omitted when the value was added. */
  before?: T;
  /** Omitted when the value was removed. */
  after?: T;
  className?: string;
  children: ValueChangeRender<T>;
}

/**
 * Lays out a version-history diff. Do not pass unchanged values — render those
 * directly. Presence of each side is the change:
 * - after only → added
 * - before only → removed
 * - both → replaced (old removed, new added, arrow between)
 */
export const ValueChange = <T,>({
  before,
  after,
  className,
  children,
}: ValueChangeProps<T>): ReactNode => {
  if (before === undefined && after === undefined) {
    return;
  }

  return (
    <span
      className={classes(
        "inline-flex items-center gap-1 align-middle",
        className
      )}
    >
      {before !== undefined && children(before, { styleChange: "removed" })}
      {before !== undefined && after !== undefined && (
        <ChangeIcon className="shrink-0 text-base" color="inherit" />
      )}
      {after !== undefined && children(after, { styleChange: "added" })}
    </span>
  );
};
